import { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, Maximize2, Minimize2 } from "lucide-react";
import { MinimapView } from "./sandbox/MinimapView";
import { SizeFrame } from "@pineseed/gui/component";
import { WorkspaceKeyAcpAvatarChat } from "@pineseed/interface-gui/component/WorkspaceKeyAcpAvatarChat";
import { useAdmin } from "../context/AdminContext";

function SandboxContent() {
  const { acpAvatarChatConfig, isAuthenticated } = useAdmin();

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4">
      {/*
        Add your test components here.

        Example:
        import { MyTestComponent } from '../sandbox/MyTestComponent';
        import { useAdmin } from '../context/AdminContext';

        const { adminApp, tagger, participant } = useAdmin();

        <MyTestComponent
          workspaceId={adminApp.workspaceId}
          namespace={adminApp.namespace}
        />
      */}

      <WorkspaceKeyAcpAvatarChat config={acpAvatarChatConfig} isAuthenticated={isAuthenticated} />

      <SizeFramedApp />
    </div>
  );
}

function SizeFramedApp() {
  /*
    Example: Toggle to control SizeFrame persistence
    - Boolean state persisted to localStorage
    - Conditionally pass persistenceKey prop to SizeFrame
    - Use headerActions prop to render toggle in SizeFrame header
  */
  const [persistEnabled, setPersistEnabled] = useState(() => {
    const saved = localStorage.getItem("sandbox-persist-toggle");
    return saved ? saved === "true" : false;
  });

  useEffect(() => {
    localStorage.setItem("sandbox-persist-toggle", persistEnabled.toString());
  }, [persistEnabled]);

  const toggle = (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.25rem",
        cursor: "pointer",
        fontSize: "0.75rem",
        color: "#6b7280",
      }}
    >
      <input
        type="checkbox"
        checked={persistEnabled}
        onChange={(e) => setPersistEnabled(e.target.checked)}
        style={{ cursor: "pointer" }}
      />
      Persist: {persistEnabled ? "ON" : "OFF"}
    </label>
  );

  return (
    <SizeFrame
      title="Sandbox Test"
      persistenceKey={persistEnabled ? "admin-sandbox-size" : undefined}
      headerActions={toggle}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
          background: "linear-gradient(135deg, #6f7333 0%, #7e88aa 100%)",
          color: "white",
          fontSize: "1.5rem",
          fontWeight: 600,
          fontFamily: "system-ui, sans-serif",
        }}
      >
        Hello World
      </div>
    </SizeFrame>
  );
}

const SANDBOX_VISIBLE_KEY = "admin-sandbox-visible";
const SANDBOX_SIDE_KEY = "admin-sandbox-side";
const SANDBOX_WIDTH_KEY = "admin-sandbox-width";
const MINIMAP_VISIBLE_KEY = "admin-minimap-visible";
const MINIMAP_SIDE_KEY = "admin-minimap-side";
const SANDBOX_DETACHED_KEY = "admin-sandbox-detached";

const DEFAULT_WIDTH = 400;
const MIN_WIDTH = 200;
const MAX_WIDTH = window.innerWidth * 0.9;

export type Side = "left" | "right";

export interface SandboxPaneProps {
  isVisible: boolean;
  onVisibilityChange: (visible: boolean) => void;
  side: Side;
  onSideChange: (side: Side) => void;
}

export function SandboxPane({
  isVisible,
  onVisibilityChange,
  side,
  onSideChange,
}: SandboxPaneProps) {
  const [width, setWidth] = useState(() => {
    const saved = localStorage.getItem(SANDBOX_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });

  const [isResizing, setIsResizing] = useState(false);
  const paneRef = useRef<HTMLDivElement>(null);

  // Detached (modal) state
  const [isDetached, setIsDetached] = useState(() => {
    const saved = localStorage.getItem(SANDBOX_DETACHED_KEY);
    return saved ? saved === 'true' : false;
  });

  useEffect(() => {
    localStorage.setItem(SANDBOX_DETACHED_KEY, isDetached.toString());
  }, [isDetached]);

  // Minimap state
  const [minimapVisible, setMinimapVisible] = useState(() => {
    const saved = localStorage.getItem(MINIMAP_VISIBLE_KEY);
    return saved ? saved === "true" : false; // Default hidden
  });

  const [minimapSide, setMinimapSide] = useState<"left" | "right">(() => {
    const saved = localStorage.getItem(MINIMAP_SIDE_KEY) as
      | "left"
      | "right"
      | null;
    return saved || "left";
  });

  // Persist minimap state
  useEffect(() => {
    localStorage.setItem(MINIMAP_VISIBLE_KEY, minimapVisible.toString());
  }, [minimapVisible]);

  useEffect(() => {
    localStorage.setItem(MINIMAP_SIDE_KEY, minimapSide);
  }, [minimapSide]);

  // Persist width
  useEffect(() => {
    localStorage.setItem(SANDBOX_WIDTH_KEY, width.toString());
  }, [width]);

  // Handle resize
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!paneRef.current) return;

      const rect = paneRef.current.getBoundingClientRect();
      let newWidth: number;

      if (side === "left") {
        newWidth = e.clientX - rect.left;
      } else {
        newWidth = rect.right - e.clientX;
      }

      newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, side]);

  const toggleSide = () => {
    onSideChange(side === "left" ? "right" : "left");
  };

  if (!isVisible) return null;

  const sandboxContent = (
    <>
      {/* Minimap (left side) */}
      {minimapVisible && minimapSide === 'left' && (
        <MinimapView
          side={minimapSide}
          onSideChange={setMinimapSide}
        />
      )}

      {/* Main Sandbox Content */}
      <SandboxContent />

      {/* Minimap (right side) */}
      {minimapVisible && minimapSide === 'right' && (
        <MinimapView
          side={minimapSide}
          onSideChange={setMinimapSide}
        />
      )}
    </>
  );

  if (isDetached) {
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2"
        onClick={() => setIsDetached(false)}
      >
        <div 
          className="bg-white dark:bg-gray-900 rounded-lg flex flex-col overflow-hidden"
          style={{ width: '90vw', height: '90vh' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="h-8 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between px-3 bg-gray-50 dark:bg-gray-900">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Sandbox</span>
            <button
              onClick={() => setIsDetached(false)}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
              title="Close"
            >
              <Minimize2 size={14} className="text-gray-600 dark:text-gray-400" />
            </button>
          </div>
          <div className="flex-1 flex overflow-hidden">
            {sandboxContent}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={paneRef}
      className={`relative flex-shrink-0 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 flex flex-col ${
        side === "left" ? "border-r" : "border-l"
      }`}
      style={{ width: `${width}px` }}
    >
      {/* Header */}
      <div className="h-8 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between px-3 bg-gray-50 dark:bg-gray-900">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Sandbox
          </span>
          <button
            onClick={() => setMinimapVisible(!minimapVisible)}
            className={`text-[10px] px-1.5 py-0.5 rounded transition-colors ${
              minimapVisible
                ? "bg-blue-500 text-white hover:bg-blue-600"
                : "hover:bg-gray-200 dark:hover:bg-gray-700"
            }`}
            title={minimapVisible ? "Hide Minimap" : "Show Minimap"}
          >
            Map
          </button>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsDetached(!isDetached)}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
            title={isDetached ? "Dock" : "Detach"}
          >
            {isDetached ? (
              <Minimize2 size={14} className="text-gray-600 dark:text-gray-400" />
            ) : (
              <Maximize2 size={14} className="text-gray-600 dark:text-gray-400" />
            )}
          </button>
          <button
            onClick={toggleSide}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
            title={`Move to ${side === "left" ? "right" : "left"}`}
          >
            {side === "left" ? (
              <ChevronRight
                size={14}
                className="text-gray-600 dark:text-gray-400"
              />
            ) : (
              <ChevronLeft
                size={14}
                className="text-gray-600 dark:text-gray-400"
              />
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex overflow-hidden sandbox-content">
        {sandboxContent}
      </div>

      {/* Resize Handle */}
      <div
        className={`absolute top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-500 transition-colors ${
          side === "left" ? "right-0" : "left-0"
        } ${isResizing ? "bg-blue-500" : ""}`}
        onMouseDown={handleMouseDown}
        style={{
          [side === "left" ? "right" : "left"]: "-2px",
          width: "4px",
        }}
      />
    </div>
  );
}

// Export persistence keys for use in parent
export { SANDBOX_VISIBLE_KEY, SANDBOX_SIDE_KEY };
